import type { CoachIdentityActivationStatus } from "@/lib/behavior/behaviorContracts";
import type { CoachVeeraPrepEvidenceRef } from "@/lib/behavior/viewModels/buildCoachSessionPrepVeeraBrief";
import { isCoachVisibleLeaderMemoryVisibility } from "@/lib/leaderMemoryVisibility";

export interface CoachSessionPrepCoachVeeraPageContextData {
  source: "coach-veera-on-demand";
  historyScope: "rolling_90d";
  runtimeStatus: "configured" | "defaulted";
  coachIdentityStatus: CoachIdentityActivationStatus;
  coachIdentityLensLabel: string | null;
  coachIdentityFrameworkPackId: string | null;
  coachIdentityFrameworkPackLabel: string | null;
  coachIdentityVoiceSummary: string | null;
  coachIdentityVoiceCustomizationStatus: "customized" | "framework_default" | "default";
  coachIdentityReferenceCount: number;
  coachIdentityParsedReferenceCount: number;
  coachIdentityPreferredLanguage: string[];
  coachIdentityAvoidLanguage: string[];
  coachIdentityGuardrails: string[];
  leaderLeadershipLens: string | null;
  leaderApprovedContext: Array<{
    title: string;
    memoryLabel: string;
    summary: string;
    visibility: "share_summary_with_coach" | "share_full_source_with_coach";
  }>;
  continuityHighlights: string[];
  fallbackTopics: string[];
}

export interface CoachSessionPrepCoachVeeraResponse {
  message?: string;
  error?: string;
  sessionId?: string;
  evidenceRefs?: Array<{ id: string; label: string; route: string | null }>;
  evidenceHealth?: {
    projectionFresh?: boolean;
    projectionLastUpdatedAt?: string | null;
    citationCoverage?: number;
  } | null;
  prepHelper?: {
    highestLeverageTopic?: string;
    followUpQuestions?: string[];
  } | null;
  conversationState?: CoachSessionPrepCoachVeeraConversationState | null;
}

export interface CoachSessionPrepCoachVeeraConversationState {
  sessionId: string;
  surfaceRole: "coach";
  currentGoal: string | null;
  currentHypothesis: string | null;
  recommendedNextMove: string | null;
  unresolvedQuestion: string | null;
  confidence: "low" | "medium" | "high";
  thinEvidence: boolean;
  contradictionCount: number;
  freshness: "fresh" | "recent" | "stale" | "unknown" | null;
  stateVersion: number;
}

export interface CoachSessionPrepCoachVeeraAnswerState {
  answer: string;
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  projectionFresh: boolean | null;
  projectionLastUpdatedAt: string | null;
  citationCoverage: number | null;
  highestLeverageTopic: string | null;
  followUpQuestions: string[];
  sessionId: string | null;
  conversationState: CoachSessionPrepCoachVeeraConversationState | null;
}

function pickFirstDistinct(values: Array<string | null | undefined>, excluded: string[] = []): string | null {
  const excludedSet = new Set(excluded);
  for (const value of values) {
    const normalized = value?.replace(/\s+/g, " ").trim();
    if (!normalized || excludedSet.has(normalized)) continue;
    return normalized;
  }
  return null;
}

const APPROVAL_MARKER_REGEX = /\[Approval:\s*(Required|Not Required)\]/gi;

function dedupeStrings(values: Array<string | null | undefined>, limit = 5): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value?.replace(/\s+/g, " ").trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }
  return result;
}

export function stripCoachVeeraApprovalMarkers(value: string): string {
  return value
    .replace(APPROVAL_MARKER_REGEX, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function buildCoachSessionPrepCoachVeeraPageContext(params: {
  runtimeStatus: "configured" | "defaulted";
  coachIdentityStatus?: CoachIdentityActivationStatus;
  coachIdentityLensLabel?: string | null;
  coachIdentityFrameworkPackId?: string | null;
  coachIdentityFrameworkPackLabel?: string | null;
  coachIdentityVoiceSummary?: string | null;
  coachIdentityVoiceCustomizationStatus?: "customized" | "framework_default" | "default";
  coachIdentityReferenceCount?: number;
  coachIdentityParsedReferenceCount?: number;
  coachIdentityPreferredLanguage?: string[];
  coachIdentityAvoidLanguage?: string[];
  coachIdentityGuardrails?: string[];
  leaderLeadershipLens?: string | null;
  leaderApprovedContext?: Array<{
    title: string;
    memoryLabel: string;
    summary: string;
    visibility: "share_summary_with_coach" | "share_full_source_with_coach";
  }>;
  continuityHighlights?: string[];
  fallbackTopics?: string[];
}): CoachSessionPrepCoachVeeraPageContextData {
  return {
    source: "coach-veera-on-demand",
    historyScope: "rolling_90d",
    runtimeStatus: params.runtimeStatus,
    coachIdentityStatus: params.coachIdentityStatus || "draft",
    coachIdentityLensLabel: params.coachIdentityLensLabel || null,
    coachIdentityFrameworkPackId: params.coachIdentityFrameworkPackId || null,
    coachIdentityFrameworkPackLabel: params.coachIdentityFrameworkPackLabel || null,
    coachIdentityVoiceSummary: params.coachIdentityVoiceSummary || null,
    coachIdentityVoiceCustomizationStatus: params.coachIdentityVoiceCustomizationStatus || "default",
    coachIdentityReferenceCount: params.coachIdentityReferenceCount ?? 0,
    coachIdentityParsedReferenceCount: params.coachIdentityParsedReferenceCount ?? 0,
    coachIdentityPreferredLanguage: dedupeStrings(params.coachIdentityPreferredLanguage || [], 5),
    coachIdentityAvoidLanguage: dedupeStrings(params.coachIdentityAvoidLanguage || [], 6),
    coachIdentityGuardrails: dedupeStrings(params.coachIdentityGuardrails || [], 4),
    leaderLeadershipLens: params.leaderLeadershipLens || null,
    leaderApprovedContext: (params.leaderApprovedContext || [])
      .filter((item) => isCoachVisibleLeaderMemoryVisibility(item.visibility))
      .map((item) => ({
        title: item.title,
        memoryLabel: item.memoryLabel,
        summary: item.summary,
        visibility: item.visibility,
      }))
      .slice(0, 5),
    continuityHighlights: dedupeStrings(params.continuityHighlights || [], 4),
    fallbackTopics: dedupeStrings(params.fallbackTopics || [], 5),
  };
}

export function buildCoachSessionPrepCoachVeeraAnswerState(
  response: CoachSessionPrepCoachVeeraResponse,
): CoachSessionPrepCoachVeeraAnswerState {
  const message = typeof response.message === "string" ? response.message : "";
  const answer = stripCoachVeeraApprovalMarkers(message);

  if (!answer.trim()) {
    throw new Error("No response returned");
  }

  return {
    answer,
    evidenceRefs: (response.evidenceRefs || [])
      .filter((entry) => !!entry.route)
      .map((entry) => ({
        id: entry.id,
        label: entry.label,
        route: entry.route || "",
      }))
      .slice(0, 6),
    projectionFresh:
      typeof response.evidenceHealth?.projectionFresh === "boolean"
        ? response.evidenceHealth.projectionFresh
        : null,
    projectionLastUpdatedAt: response.evidenceHealth?.projectionLastUpdatedAt || null,
    citationCoverage:
      typeof response.evidenceHealth?.citationCoverage === "number"
        ? response.evidenceHealth.citationCoverage
        : null,
    highestLeverageTopic: response.prepHelper?.highestLeverageTopic || null,
    followUpQuestions: (response.prepHelper?.followUpQuestions || []).slice(0, 5),
    sessionId: typeof response.sessionId === "string" && response.sessionId ? response.sessionId : null,
    conversationState:
      response.conversationState
      && response.conversationState.surfaceRole === "coach"
      && typeof response.conversationState.sessionId === "string"
      && response.conversationState.sessionId
        ? {
            sessionId: response.conversationState.sessionId,
            surfaceRole: "coach",
            currentGoal: response.conversationState.currentGoal || null,
            currentHypothesis: response.conversationState.currentHypothesis || null,
            recommendedNextMove: response.conversationState.recommendedNextMove || null,
            unresolvedQuestion: response.conversationState.unresolvedQuestion || null,
            confidence: response.conversationState.confidence || "medium",
            thinEvidence: response.conversationState.thinEvidence === true,
            contradictionCount: response.conversationState.contradictionCount ?? 0,
            freshness: response.conversationState.freshness || null,
            stateVersion: Math.max(1, response.conversationState.stateVersion ?? 1),
          }
        : null,
  };
}

export function buildCoachSessionPrepCoachVeeraFallbackAnswer(params: {
  pageContextData: CoachSessionPrepCoachVeeraPageContextData;
}): CoachSessionPrepCoachVeeraAnswerState | null {
  const { pageContextData } = params;
  const carryForward = pickFirstDistinct(pageContextData.continuityHighlights);
  const whatChanged = pickFirstDistinct(pageContextData.fallbackTopics, carryForward ? [carryForward] : []);
  const nextMove = pickFirstDistinct(
    [...pageContextData.fallbackTopics, ...pageContextData.continuityHighlights],
    [carryForward, whatChanged].filter((value): value is string => Boolean(value)),
  );

  if (!carryForward && !whatChanged && !nextMove) {
    return null;
  }

  const lines = [
    "I couldn't reach live Coach Veera, so this is the current prep read from the evidence already on this page.",
    whatChanged ? `What changed: ${whatChanged}` : null,
    carryForward ? `Carry forward: ${carryForward}` : null,
    nextMove ? `Next move to probe: ${nextMove}` : null,
  ].filter((line): line is string => Boolean(line));

  return {
    answer: lines.join("\n\n"),
    evidenceRefs: [],
    projectionFresh: null,
    projectionLastUpdatedAt: null,
    citationCoverage: null,
    highestLeverageTopic: null,
    followUpQuestions: [],
    sessionId: null,
    conversationState: null,
  };
}
